import { apiFetch } from "./api";
import { currentSite } from "./sites";
import { getSession } from "./auth";
import { alCambiarDeSesion } from "./estadoDeSesion";

// Analítica de uso. La app corre SIEMPRE con conexión, así que las sesiones se
// envían en cuanto ocurren; la cola solo absorbe un fallo puntual de red y se
// reintenta en el siguiente envío. No es un almacén offline: si el navegador se
// cierra con algo pendiente, se pierde, y es aceptable — es telemetría, no datos
// de una persona.

export interface UsageSession {
  deviceName: string;
  siteCode: string;
  profile: string | null;
  moduleSlug: string | null;
  startedAt: string;
  durationSeconds: number;
  result: string | null;
  meta?: Record<string, unknown>;
}

const QUEUE_KEY = "ikids.analytics.queue";
const DEVICE_KEY = "ikids.deviceName";

/*
 * La cola muere en CUALQUIER transición de sesión, no solo al salir a propósito.
 * `sessionStorage` la mata al cerrar la pestaña; esto cubre el caso de verdad en
 * un equipo compartido: alguien sale y entra otro sin recargar la página.
 */
alCambiarDeSesion(() => {
  try { sessionStorage.removeItem(QUEUE_KEY); } catch { /* modo privado o cuota */ }
});

function deviceName(): string {
  let d = localStorage.getItem(DEVICE_KEY);
  if (!d) {
    d = `web-${Math.abs(hashString(navigator.userAgent + screen.width + screen.height)) % 9000 + 1000}`;
    localStorage.setItem(DEVICE_KEY, d);
  }
  return d;
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
}

function readQueue(): UsageSession[] {
  try {
    return JSON.parse(sessionStorage.getItem(QUEUE_KEY) ?? "[]") as UsageSession[];
  } catch {
    return [];
  }
}

function writeQueue(q: UsageSession[]): void {
  sessionStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

/*
 * El residencial del equipo. `/analytics/sessions` no lleva token —su identidad es
 * el equipo, no una persona— así que la sede tiene que viajar en el cuerpo, y es el
 * único sitio de la app donde sigue siendo así.
 *
 * Se resuelve UNA vez desde `GET /sites` (que sí va autenticado) y se guarda: un
 * kiosco está siempre en el mismo residencial. Antes era `SITE.siteCode` =
 * `"QUINTANA-SJ"`, que no existe en `sites`: el backend descartaba cada sesión
 * (`rejected++`) y la analítica de todos los residenciales se perdía entera.
 *
 * Si aún no se conoce, la sesión se encola SIN sede y se reintenta luego, en vez de
 * mandar un código inventado que el servidor rechazaría sin remedio.
 */
const SITE_KEY = "ikids.device.siteCode";

function sedeConocida(): string | null {
  return localStorage.getItem(SITE_KEY);
}

async function resolverSede(): Promise<string | null> {
  /*
   * La sede de QUIEN ESTA DENTRO manda sobre la que quedo guardada en el equipo.
   *
   * Antes era al reves y `localStorage` ganaba siempre. En un navegador donde
   * alguien habia entrado como admin, el siguiente en usarlo —un participante—
   * heredaba aquella sede: se le mostraba un residencial que no es el suyo y, peor,
   * sus sesiones se guardaban ahi. Con la analitica atribuida a la persona eso
   * mete su actividad en el residencial equivocado, que es exactamente lo que se
   * factura. Visto el 2026-08-19: un nino de Jardines de Campo Rico aparecia en
   * Villa Valle Verde.
   *
   * `currentSite()` devuelve algo solo cuando el token da acceso a UNA sede —un
   * participante o una bibliotecaria—. Para un admin devuelve null y se cae a la
   * sede guardada, que es la del equipo y sigue siendo lo correcto en un kiosco.
   */
  const s = await currentSite();
  if (s) {
    localStorage.setItem(SITE_KEY, s.code);
    return s.code;
  }
  return sedeConocida();
}

export function recordSession(s: Omit<UsageSession, "deviceName" | "siteCode">): void {
  // Sin sede aqui a proposito: la resuelve `flush` con la de quien esta dentro.
  // Ponerla desde `localStorage` en este punto volveria a colar la sede heredada.
  const full: UsageSession = { ...s, deviceName: deviceName(), siteCode: "" };
  const q = readQueue();
  q.push(full);
  writeQueue(q);
  void flush();
}

/*
 * Aquí había un "historial para el panel admin" en `localStorage`: las últimas
 * 500 sesiones del equipo, de TODAS las personas que lo usaran, que sobrevivía a
 * cerrar sesión y al navegador. Nadie lo leía —el panel saca el uso de
 * `GET /analytics/resumen`—, así que era actividad de unas personas guardada a la
 * vista de las siguientes sin ningún propósito. Se fue, y lo que ya hubiera
 * guardado en los equipos se borra al cargar.
 */
try { localStorage.removeItem("ikids.analytics.history"); } catch { /* modo privado */ }

export async function flush(): Promise<void> {
  const q = readQueue();
  if (q.length === 0) return;

  /*
   * Se pierde lo que quede sin enviar al cambiar de sesión, y es lo correcto: la
   * analítica de esta plataforma es prescindible y siempre conectada. Atribuirla a
   * quien no es, no lo es.
   */
  if (!getSession()) return;

  // Una sesión sin sede el servidor la DESCARTA (no la puede atribuir a nada). Se
  // rellena aquí, al enviar, para que las que se grabaron antes de conocer el
  // residencial —el arranque en frío del equipo— no se pierdan.
  const sede = await resolverSede();
  if (!sede) return; // Todavía no se sabe dónde está el equipo: se reintenta luego.
  const sessions = q.map((s) => (s.siteCode ? s : { ...s, siteCode: sede }));

  try {
    const res = await apiFetch("/analytics/sessions", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sessions }),
    });
    if (res.ok) writeQueue([]);
  } catch {
    // Fallo puntual de red: queda en la cola y se reintenta con el próximo envío.
  }
}
